import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { SiteLayout } from "@/components/site/Layout";
import { useAuth } from "@/context/AuthContext";
import { api, gbp, longDate, isoDate, STATUS_STYLES, statusLabel, formatApiError } from "@/lib/api";

const CancelButton = ({ b, onDone }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="outline" size="sm" className="rounded-full" data-testid={`booking-cancel-button-${b.id}`}>Cancel</Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Cancel this appointment?</AlertDialogTitle>
        <AlertDialogDescription data-testid="cancel-dialog-refund-note">
          {b.refund_eligible
            ? `You'll get a full refund of ${gbp(b.amount)} to your card, usually within 5–10 working days.`
            : `This is less than ${b.cutoff_hours} hours before your appointment, so the clinic's policy means no refund will be issued.`}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="cancel-dialog-keep-button">Keep it</AlertDialogCancel>
        <AlertDialogAction data-testid="cancel-dialog-confirm-button" onClick={async () => {
          try {
            const { data } = await api.post(`/me/bookings/${b.id}/cancel`);
            toast.success(data.refund_status === "refunded" ? `Cancelled — ${gbp(data.refund_amount)} refunded` : "Appointment cancelled");
            onDone();
          } catch (e) { toast.error(formatApiError(e)); }
        }}>{b.refund_eligible ? "Cancel & refund" : "Cancel without refund"}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const BookingRow = ({ b, onDone, upcoming }) => (
  <div className="rounded-2xl border border-border bg-card p-5 flex flex-wrap items-center gap-4 justify-between" data-testid={`my-booking-${b.id}`}>
    <div className="flex items-center gap-4">
      <span className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><CalendarDays className="h-5 w-5" /></span>
      <div>
        <p className="font-display font-bold">{b.service_name}</p>
        <p className="text-sm text-muted-foreground">
          <Link to={`/clinic/${b.clinic_slug}`} className="hover:text-primary">{b.clinic_name}</Link> · {b.practitioner_name}
        </p>
        <p className="text-sm">{longDate(b.date)} · {b.start}–{b.end}</p>
        {upcoming && b.can_cancel && (
          <p className="text-xs text-muted-foreground mt-0.5" data-testid={`my-booking-policy-${b.id}`}>
            {b.refund_eligible ? `Free cancellation until ${b.cutoff_hours}h before` : "Inside cancellation window — no refund"}
          </p>
        )}
        {b.status === "cancelled" && (
          <p className="text-xs mt-0.5" data-testid={`my-booking-refund-${b.id}`}>
            {b.refund_status === "refunded" ? <span className="text-emerald-700">Refunded {gbp(b.refund_amount)}</span> : <span className="text-muted-foreground">Not refunded</span>}
          </p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-3">
      <span className="font-semibold text-sm">{gbp(b.amount)}</span>
      <span className={`text-xs capitalize rounded-full border px-2.5 py-1 ${STATUS_STYLES[b.status]}`} data-testid={`my-booking-status-${b.id}`}>{statusLabel(b.status)}</span>
      {upcoming && b.can_cancel && <CancelButton b={b} onDone={onDone} />}
    </div>
  </div>
);

export default function Account() {
  const { user } = useAuth();
  const [tab, setTab] = useState("upcoming");
  const [list, setList] = useState(null);
  const load = () => api.get("/me/bookings").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const today = isoDate(new Date());
  const upcoming = (list || []).filter((b) => b.date >= today && b.status === "confirmed").sort((a, b) => (a.date + a.start).localeCompare(b.date + b.start));
  const past = (list || []).filter((b) => !(b.date >= today && b.status === "confirmed"));
  const shown = tab === "upcoming" ? upcoming : past;

  return (
    <SiteLayout>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <p className="eyebrow">My account</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-2" data-testid="account-title">Hi {user?.name?.split(" ")[0]}</h1>
        <Tabs value={tab} onValueChange={setTab} className="mt-8">
          <TabsList>
            <TabsTrigger value="upcoming" data-testid="account-tab-upcoming">Upcoming ({upcoming.length})</TabsTrigger>
            <TabsTrigger value="past" data-testid="account-tab-past">Past & cancelled ({past.length})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="mt-6 grid gap-3">
          {shown.map((b) => <BookingRow key={b.id} b={b} onDone={load} upcoming={tab === "upcoming"} />)}
          {list && shown.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center" data-testid="account-empty">
              <p className="text-muted-foreground">No appointments here yet.</p>
              <Button className="rounded-full mt-4" asChild data-testid="account-find-clinic-button"><Link to="/clinics">Find a clinic</Link></Button>
            </div>
          )}
        </div>
      </div>
    </SiteLayout>
  );
}
